import { practiceClient } from "@/lib/practice-client";
import { getStreakDisplay } from "@/lib/streakSystem";
import { calendarDay, deviceTimezone } from "@/shared/streak";
import { useCallback, useEffect, useSyncExternalStore } from "react";

export function useStreak(userId: string | null) {
  const subscribe = useCallback((fn: () => void) => practiceClient.subscribe(userId, fn), [userId]);
  const snapshot = useCallback(() => practiceClient.snapshot(userId), [userId]);
  const state = useSyncExternalStore(subscribe, snapshot, snapshot);
  const refreshStreak = useCallback(async () => {
    if (userId) await practiceClient.refresh(userId);
  }, [userId]);
  const updateStreak = useCallback(
    async (timestamp?: number) => {
      if (userId) await practiceClient.record(userId, timestamp);
    },
    [userId]
  );
  useEffect(() => {
    if (!userId) return;
    void refreshStreak();
    const refresh = () => {
      if (document.visibilityState === "visible") void refreshStreak();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    const onStorage = (event: StorageEvent) => {
      if (event.key === `practice-days:v1:${userId}`) refresh();
    };
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", refresh);
    let date = calendarDay(
      Date.now(),
      practiceClient.snapshot(userId).streak?.timezone || deviceTimezone()
    );
    const timer = setInterval(() => {
      const today = calendarDay(
        Date.now(),
        practiceClient.snapshot(userId).streak?.timezone || deviceTimezone()
      );
      if (date !== today) {
        date = today;
        refresh();
      }
    }, 15000);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [refreshStreak, userId]);
  return {
    streakData: state.streak,
    streakDisplay:
      state.streak && !state.error && state.streak.timezone ? getStreakDisplay(state.streak) : null,
    loading: state.loading,
    error: state.error,
    updateStreak,
    refreshStreak,
  };
}
